import { Box, Text } from '@chakra-ui/react';
import React, { useState, useMemo, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MentionsInput, Mention } from 'react-mentions';

import { NAMESPACE } from '@app/i18n';
import {
  formatApproximateCharacterCount,
  formatCharacterCount,
} from '@app/i18n/formatters/character-count';

export interface TaggedTextareaItem {
  id: string;
  display: string;
  isNew?: boolean;
}

export interface TaggedTextareaProps {
  value: string;
  onChange: (
    event: { target: { value: string } },
    newValue?: string,
    newPlainTextValue?: string,
    mentions?: { id: string; display: string }[],
  ) => void;
  trigger: string;
  data:
    | TaggedTextareaItem[]
    | ((
        searchText: string,
        callback: (data: TaggedTextareaItem[]) => void,
      ) => void);
  placeholder?: string;
  maxLength?: number;
  showCharCount?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  allowSpaceInQuery?: boolean;
  allowSuggestionsAboveCursor?: boolean;
  testId?: string;
  renderSuggestion?: (suggestion: TaggedTextareaItem) => React.ReactNode;
  displayTransform?: (id: string, display: string) => string;
  onAdd?: (id: string, display: string) => void;
}

const defaultRenderSuggestion = (suggestion: TaggedTextareaItem) => (
  <Box
    borderRadius="md"
    px={3}
    _hover={{ bg: 'bg.subtle' }}
    cursor="pointer"
    fontSize="sm"
    color="gray.fg"
    minH="32px"
    display="flex"
    alignItems="center"
    transition="all 0.2s"
  >
    {suggestion?.display || ''}
  </Box>
);

// Helper to calculate plain text length from mention format
// Converts @[display](id) to just "display" for accurate character counting
const getPlainTextLength = (value: string): number => {
  if (!value) return 0;
  // Replace mention format @[display](id) with just the display text
  const plainText = value.replace(/@\[([^\]]+)\]\([^)]+\)/g, '$1');
  return plainText.length;
};

export const TaggedTextarea = forwardRef<
  HTMLTextAreaElement,
  TaggedTextareaProps
>(function TaggedTextarea(
  {
    value,
    onChange,
    trigger,
    data,
    placeholder,
    maxLength,
    showCharCount = false,
    isLoading = false,
    disabled = false,
    allowSpaceInQuery = true,
    allowSuggestionsAboveCursor = false,
    testId,
    renderSuggestion = defaultRenderSuggestion,
    displayTransform,
    onAdd,
  },
  ref,
) {
  const { t } = useTranslation(NAMESPACE.COMMON);
  const resolvedPlaceholder = placeholder ?? t('startTypingPlaceholder');
  const [suggestionsPortalHost, setSuggestionsPortalHost] = useState<
    HTMLDivElement | undefined
  >(undefined);

  // Calculate plain text length from value - more efficient than useEffect + state
  // Automatically stays in sync with value prop changes (form resets, initial load)
  const plainTextLength = useMemo(() => getPlainTextLength(value), [value]);

  const handleChange = (
    event: { target: { value: string } },
    newValue: string,
    newPlainTextValue: string,
    mentions: { id: string; display: string }[],
  ) => {
    if (maxLength) {
      const limitedValue = event.target.value.slice(0, maxLength);
      onChange(
        { target: { value: limitedValue } },
        newValue,
        newPlainTextValue,
        mentions,
      );
    } else {
      onChange(event, newValue, newPlainTextValue, mentions);
    }
  };

  // Callback ref to set the portal host element in state
  const setSuggestionsPortalRef = (node: HTMLDivElement | null) => {
    if (node) {
      setSuggestionsPortalHost(node);
    }
  };

  return (
    <>
      <MentionsInput
        inputRef={ref as React.Ref<HTMLTextAreaElement>}
        value={value}
        onChange={handleChange}
        placeholder={resolvedPlaceholder}
        disabled={disabled}
        className="chakraTaggedTextarea"
        allowSpaceInQuery={allowSpaceInQuery}
        suggestionsPortalHost={suggestionsPortalHost}
        allowSuggestionsAboveCursor={allowSuggestionsAboveCursor}
        data-testid={testId}
      >
        <Mention
          renderSuggestion={(
            suggestion,
            _search,
            _highlightedDisplay,
            _index,
            _focused,
          ) => renderSuggestion(suggestion as TaggedTextareaItem)}
          className="chakraTaggedTextareaSuggestion"
          trigger={trigger}
          data={data}
          isLoading={isLoading}
          displayTransform={
            displayTransform || ((_id: string, display: string) => display)
          }
          markup="@[__display__](__id__)"
          onAdd={
            onAdd ? (id, display) => onAdd(String(id), display) : undefined
          }
        />
      </MentionsInput>
      {showCharCount && (
        <Text fontSize="sm" color="fg.muted" mt={1} userSelect="none">
          {maxLength
            ? formatCharacterCount(t, plainTextLength, maxLength)
            : formatApproximateCharacterCount(t, plainTextLength)}
        </Text>
      )}
      <div
        ref={setSuggestionsPortalRef}
        style={{ position: 'relative', zIndex: 10000 }}
      />
    </>
  );
});
